import { FC } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { CUSTOM_EMOJI_MAP } from '../../emoji-theme-default';
import { useSoundHandler } from '../../hooks/use-sound-handler';
import { ChatSendButton } from '../ChatSendButton';
import { RoundedEmojiInput } from '../RoundedEmojiInput';
import { ChatTextFieldProps } from './mobile';

const buttonDimension = css`
    width: 3.5rem;
    height: 3.5rem;
`;

const ChatTextFieldContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    width: 100%;
    gap: 0.25rem;
`;

const InputWrapper = styled.div`
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
`;

const EmoticonWrapper = styled.div<{ focused: boolean; disabled: boolean }>`
    ${buttonDimension};

    position: absolute;
    top: 0.25rem;
    right: ${({ focused }) => (focused ? '0.55rem' : '0.325rem')};

    ${({ disabled }) => `
        ${
            disabled
                ? `
            cursor: not-allowed;
            opacity: 0.5;
        `
                : `
        cursor: pointer;
        `
        }
    `};
`;

const EmoticonButton = styled.img<{ focused: boolean; disabled: boolean }>`
    ${buttonDimension};

    ${({ disabled }) => `
        ${
            disabled
                ? `
            pointer-events: none;
        `
                : ''
        }
    `};
`;

const ButtonMask = styled.div`
    ${buttonDimension};
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0.5;
    border-radius: 50%;
    background-color: #000;
`;

export const ChatTextFieldDesktop: FC<ChatTextFieldProps> = ({
    focused,
    message,
    disabled,
    mentions,
    inputRef,
    placeholder,
    disabledButtons,
    onEmoticon,
    onSend,
    onFocus,
    onChange,
    onEnter,
    ...props
}) => {
    const theme = useTheme();
    const { emoticon } = theme.images;

    const $disabled = disabled || disabledButtons;
    const $onEmoticon = useSoundHandler(onEmoticon, 'click');

    return (
        <ChatTextFieldContainer {...props}>
            <InputWrapper>
                <RoundedEmojiInput
                    emojis={CUSTOM_EMOJI_MAP}
                    value={message}
                    focused={focused}
                    disabled={disabled}
                    ref={inputRef}
                    placeholder={placeholder}
                    onFocus={onFocus}
                    onChange={onChange}
                    onKeyDown={onEnter}
                />
                <EmoticonWrapper disabled={$disabled} focused={focused}>
                    <EmoticonButton
                        src={emoticon}
                        alt="Emoticon Button Image"
                        focused={focused}
                        onClick={$onEmoticon}
                        disabled={$disabled}
                    />
                    {$disabled && <ButtonMask />}
                </EmoticonWrapper>
            </InputWrapper>
            <ChatSendButton
                disabled={$disabled}
                handleClick={onSend}
                mentions={mentions}
            />
        </ChatTextFieldContainer>
    );
};
